import React, { useEffect, useState } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import {
  Package,
  Plus,
  Trash,
  ArrowClockwise,
  Warning,
  CheckCircle,
  Clock,
  ArrowSquareOut,
  Pause,
  Play,
} from "@phosphor-icons/react";
import {
  fetchStockWatches,
  createStockWatch,
  updateStockWatch,
  deleteStockWatch,
  stockWatchCheckNow,
} from "../lib/api";
import { formatApiError } from "../lib/auth";
import { toast } from "sonner";
import { StockWatchInterval } from "./StockWatchInterval";
import { StockWatchTestButton } from "./StockWatchTestButton";
import { TelegramRecipients } from "./TelegramRecipients";
import { StockWatchAutoBuy } from "./StockWatchAutoBuy";

function timeAgo(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function StatusBadge({ watch }) {
  if (watch.last_error) {
    return (
      <Badge
        variant="outline"
        className="rounded-none border-red-400 text-red-700 bg-red-50 font-mono text-[10px] uppercase"
        data-testid={`stock-status-error-${watch.id}`}
      >
        <Warning size={12} weight="bold" className="mr-1" /> Error
      </Badge>
    );
  }
  if (!watch.last_checked) {
    return (
      <Badge
        variant="outline"
        className="rounded-none border-zinc-300 text-zinc-600 font-mono text-[10px] uppercase"
      >
        <Clock size={12} weight="bold" className="mr-1" /> Pending
      </Badge>
    );
  }
  if (watch.has_stock) {
    return (
      <Badge
        className="rounded-none bg-green-600 hover:bg-green-700 text-white font-mono text-[10px] uppercase"
        data-testid={`stock-status-available-${watch.id}`}
      >
        <CheckCircle size={12} weight="bold" className="mr-1" /> Disponible
      </Badge>
    );
  }
  return (
    <Badge
      variant="outline"
      className="rounded-none border-zinc-400 text-zinc-700 font-mono text-[10px] uppercase"
      data-testid={`stock-status-outofstock-${watch.id}`}
    >
      Sin stock
    </Badge>
  );
}

function VariationStatus({ watch }) {
  const snap = watch?.last_snapshot;
  const vars = snap?.matched_variations?.length ? snap.matched_variations : snap?.variations || [];
  if (!vars.length) return null;
  return (
    <div className="mt-2 space-y-1" data-testid={`stock-watch-variations-${watch.id}`}>
      {vars.map((v) => (
        <div
          key={v.variation_id}
          className={`font-mono text-[11px] flex flex-wrap items-center gap-2 px-2 py-1 border ${
            v.in_stock ? "border-green-400 bg-green-50 text-green-800" : "border-zinc-200 bg-zinc-50 text-zinc-700"
          }`}
        >
          <span className="font-bold uppercase">{v.label}</span>
          {v.price_text && <span>· {v.price_text}</span>}
          <span className={v.in_stock ? "font-bold" : "text-red-700 font-bold"}>
            · {v.availability_text || (v.in_stock ? "Hay existencias" : "Sin existencias")}
          </span>
        </div>
      ))}
    </div>
  );
}

function AvailableLabels({ watch }) {
  const labels = watch?.last_snapshot?.available_labels || [];
  if (!labels.length) return null;
  return (
    <div className="flex flex-wrap gap-1 mt-1">
      {labels.map((l) => (
        <span
          key={l}
          className="font-mono text-[10px] uppercase tracking-wider bg-green-100 text-green-800 border border-green-300 px-1.5 py-0.5"
        >
          {l}
        </span>
      ))}
    </div>
  );
}

export function StockWatchTab() {
  const [watches, setWatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [form, setForm] = useState({ url: "", label: "", variation_filter: "" });
  const [adding, setAdding] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const data = await fetchStockWatches();
      setWatches(data);
    } catch (e) {
      toast.error(formatApiError(e) || "No se pudo cargar la lista");
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const addWatch = async () => {
    if (!form.url.trim()) {
      toast.error("Pega una URL del producto");
      return;
    }
    setAdding(true);
    try {
      await createStockWatch({
        url: form.url.trim(),
        label: form.label.trim(),
        variation_filter: form.variation_filter.trim(),
        active: true,
      });
      toast.success("Monitor añadido");
      setForm({ url: "", label: "", variation_filter: "" });
      await load();
    } catch (e) {
      toast.error(formatApiError(e) || "No se pudo añadir");
    }
    setAdding(false);
  };

  const toggleActive = async (w) => {
    try {
      await updateStockWatch(w.id, { active: !w.active });
      toast.success(w.active ? "Revisión detenida para este producto" : "Revisión reanudada");
      await load();
    } catch (e) {
      toast.error(formatApiError(e) || "Error al cambiar estado");
    }
  };

  const removeWatch = async (w) => {
    if (!window.confirm(`¿Eliminar monitor "${w.label || w.url}"?`)) return;
    try {
      await deleteStockWatch(w.id);
      toast.success("Eliminado");
      await load();
    } catch (e) {
      toast.error(formatApiError(e) || "Error al eliminar");
    }
  };

  const checkOne = async (w) => {
    setChecking(true);
    try {
      const res = await stockWatchCheckNow([w.id]);
      const r = res?.results?.[0];
      if (res?.group_report_sent) toast.success("Estado enviado al grupo de Telegram");
      if (r?.error) {
        toast.error(`Error: ${r.error}`);
      } else if (r?.has_stock) {
        toast.success("¡Disponible ahora!");
      } else {
        toast.info("Aún sin stock");
      }
      await load();
    } catch (e) {
      toast.error(formatApiError(e) || "Error al chequear");
    }
    setChecking(false);
  };

  const checkAll = async () => {
    setChecking(true);
    try {
      const res = await stockWatchCheckNow([]);
      toast.success(`Chequeados ${res?.checked || 0} items${res?.group_report_sent ? " · Reporte enviado al grupo de Telegram" : ""}`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e) || "Error al chequear");
    }
    setChecking(false);
  };

  return (
    <div className="space-y-6" data-testid="stock-watch-tab">
      {/* Header stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="border border-zinc-200 p-3 bg-white">
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1">
            <Package size={12} weight="bold" /> Monitors
          </div>
          <div className="text-3xl font-black mt-1" data-testid="stock-stat-total">
            {watches.length}
          </div>
        </div>
        <div className="border border-zinc-200 p-3 bg-white">
          <div className="font-mono text-[10px] uppercase tracking-widest text-green-700 flex items-center gap-1">
            <CheckCircle size={12} weight="bold" /> Disponibles
          </div>
          <div className="text-3xl font-black mt-1 text-green-700" data-testid="stock-stat-available">
            {watches.filter((w) => w.has_stock).length}
          </div>
        </div>
        <div className="border border-zinc-200 p-3 bg-white">
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Activos</div>
          <div className="text-3xl font-black mt-1">{watches.filter((w) => w.active).length}</div>
        </div>
        <div className="border border-zinc-200 p-3 bg-white">
          <div className="font-mono text-[10px] uppercase tracking-widest text-red-700 flex items-center gap-1">
            <Warning size={12} weight="bold" /> Errores
          </div>
          <div className="text-3xl font-black mt-1 text-red-700">
            {watches.filter((w) => w.last_error).length}
          </div>
        </div>
      </div>

      {/* Add form */}
      <div className="border-2 border-zinc-950 p-4 bg-zinc-50">
        <div className="font-mono text-[11px] uppercase tracking-widest text-[#002FA7] mb-3">
          Nuevo monitor
        </div>
        <div className="grid grid-cols-1 md:grid-cols-[2fr_1fr_1fr_auto] gap-2">
          <Input
            placeholder="URL del producto (ej: https://goniogas.com/producto/...)"
            value={form.url}
            onChange={(e) => setForm({ ...form, url: e.target.value })}
            className="rounded-none"
            data-testid="stock-watch-url-input"
          />
          <Input
            placeholder="Etiqueta (opcional)"
            value={form.label}
            onChange={(e) => setForm({ ...form, label: e.target.value })}
            className="rounded-none"
            data-testid="stock-watch-label-input"
          />
          <Input
            placeholder='Variación (ej: "Recarga")'
            value={form.variation_filter}
            onChange={(e) => setForm({ ...form, variation_filter: e.target.value })}
            className="rounded-none"
            data-testid="stock-watch-variation-input"
          />
          <Button
            onClick={addWatch}
            disabled={adding}
            className="rounded-none bg-zinc-950 hover:bg-[#002FA7] text-white font-bold uppercase tracking-wider"
            data-testid="stock-watch-add-button"
          >
            <Plus size={14} weight="bold" className="mr-1" />
            {adding ? "..." : "Añadir"}
          </Button>
        </div>
        <p className="text-xs text-zinc-500 mt-2 font-mono">
          Notifica a Telegram cuando pase de "sin stock" a "disponible".
        </p>
      </div>

      <StockWatchInterval />
      <TelegramRecipients />
      <StockWatchAutoBuy />

      {/* Actions bar */}
      <div className="flex flex-wrap gap-2 items-center justify-between">
        <div className="font-mono text-xs text-zinc-600 uppercase tracking-wider">
          {loading ? "Cargando..." : `${watches.length} monitores`}
        </div>
        <div className="flex flex-wrap gap-2">
        <StockWatchTestButton />
        <Button
          onClick={checkAll}
          disabled={checking || !watches.length}
          variant="outline"
          className="rounded-none border-zinc-950 font-mono uppercase tracking-wider text-xs"
          data-testid="stock-watch-check-all-button"
        >
          <ArrowClockwise
            size={14}
            weight="bold"
            className={`mr-2 ${checking ? "animate-spin" : ""}`}
          />
          {checking ? "Chequeando..." : "Chequear todo"}
        </Button>
        </div>
      </div>

      {/* List */}
      {watches.length === 0 && !loading ? (
        <div className="text-center py-12 border border-zinc-200 bg-zinc-50">
          <Package size={32} weight="duotone" className="mx-auto text-zinc-400 mb-2" />
          <p className="font-mono text-sm text-zinc-500">
            No hay monitores aún. Pega una URL arriba para empezar.
          </p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="stock-watch-list">
          {watches.map((w) => (
            <div
              key={w.id}
              className={`border-2 p-3 sm:p-4 bg-white ${
                w.has_stock ? "border-green-500" : "border-zinc-200"
              }`}
              data-testid={`stock-watch-row-${w.id}`}
            >
              <div className="flex flex-col sm:flex-row sm:items-start gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <StatusBadge watch={w} />
                    {w.variation_filter && (
                      <span className="font-mono text-[10px] uppercase tracking-wider bg-[#002FA7] text-white px-1.5 py-0.5">
                        {w.variation_filter}
                      </span>
                    )}
                    {!w.active && (
                      <span className="font-mono text-[10px] uppercase tracking-wider bg-zinc-200 text-zinc-700 px-1.5 py-0.5">
                        Pausado
                      </span>
                    )}
                  </div>
                  <div className="font-bold text-base mt-1 truncate">
                    {w.label || w.last_snapshot?.title || "Sin título"}
                  </div>
                  <a
                    href={w.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-mono text-xs text-[#002FA7] hover:underline inline-flex items-center gap-1 break-all"
                    data-testid={`stock-watch-link-${w.id}`}
                  >
                    {w.url}
                    <ArrowSquareOut size={10} weight="bold" />
                  </a>
                  <AvailableLabels watch={w} />
                  <VariationStatus watch={w} />
                  <div className="flex items-center gap-3 mt-2 font-mono text-[11px] text-zinc-500 uppercase tracking-wider flex-wrap">
                    <span>Última: {timeAgo(w.last_checked)}</span>
                    {w.last_available_at && (
                      <span className="text-green-700">
                        Disponible: {timeAgo(w.last_available_at)}
                      </span>
                    )}
                    {w.last_error && (
                      <span className="text-red-700 truncate max-w-[200px]">
                        {w.last_error}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-wrap">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => checkOne(w)}
                    disabled={checking}
                    className="rounded-none border-zinc-300"
                    data-testid={`stock-watch-check-${w.id}`}
                    title="Chequear ahora"
                  >
                    <ArrowClockwise size={14} weight="bold" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => toggleActive(w)}
                    className={`rounded-none font-mono uppercase text-[11px] tracking-wider ${
                      w.active
                        ? "border-red-400 text-red-700 hover:bg-red-50"
                        : "border-green-500 text-green-700 bg-green-50 hover:bg-green-100"
                    }`}
                    data-testid={`stock-watch-toggle-${w.id}`}
                    title={w.active ? "Parar de revisar" : "Reanudar revisión"}
                  >
                    {w.active ? (
                      <><Pause size={14} weight="bold" className="mr-1" /> Parar</>
                    ) : (
                      <><Play size={14} weight="bold" className="mr-1" /> Reanudar</>
                    )}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => removeWatch(w)}
                    className="rounded-none border-red-300 text-red-600 hover:bg-red-50"
                    data-testid={`stock-watch-delete-${w.id}`}
                    title="Eliminar"
                  >
                    <Trash size={14} weight="bold" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
